<script setup lang="ts">
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import {
  ExclamationTriangleIcon,
  MapPinIcon,
  SparklesIcon,
} from '@heroicons/vue/24/outline';
import Button from 'primevue/button';
import {
  getAccountGeofenceRemovalSuggestions,
  getAccountPlaceSuggestions,
} from '@gpx-spa/queries';
import { useChatStore } from '@/stores/useChatStore';
import { useSidebarIsOpen } from '@/composables/useSidebarIsOpen';
import { RIGHT_SIDEBAR } from '@/types/ui';
import { usePlaceSuggestionsDismissal } from '@/components/place-suggestions/usePlaceSuggestionsDismissal';
import * as events from '@/events';

const store = useStore();
const accountId = computed<number | null>(
  () => store.getters['auth/accountId'] ?? null,
);

// Creation (place) suggestions — "add a geofence here".
const {
  suggestions: placeSuggestions,
  count: placeCount,
  isLoading: placeIsLoading,
  isError: placeIsError,
} = getAccountPlaceSuggestions(accountId);

// Removal suggestions — "this geofence looks inactive".
const {
  count: removalCount,
  isLoading: removalIsLoading,
  isError: removalIsError,
} = getAccountGeofenceRemovalSuggestions(accountId);

const chatStore = useChatStore();
const isRightOpen = useSidebarIsOpen(RIGHT_SIDEBAR, false);

// "Later" dismissal keeps each subsystem's own semantics:
//  - creation persists the dismissed suggestion IDs (resurfaces only for new
//    ones) via usePlaceSuggestionsDismissal,
//  - removal hides for the session only and reappears on next login.
const placeSuggestionIds = computed(() =>
  (placeSuggestions.value ?? []).map((s: { id: string }) => s.id),
);
const { isDismissed: placeDismissed, dismiss: dismissPlace } =
  usePlaceSuggestionsDismissal(accountId, placeSuggestionIds);
const removalSessionDismissed = ref(false);

const showCreation = computed(
  () =>
    !placeIsLoading.value &&
    !placeIsError.value &&
    placeCount.value > 0 &&
    !placeDismissed.value,
);

const showRemoval = computed(
  () =>
    !removalIsLoading.value &&
    !removalIsError.value &&
    removalCount.value > 0 &&
    !removalSessionDismissed.value,
);

// Hide the whole banner while Scout is already open on the welcome screen or
// either suggestion thread — the banner only exists to nudge those open.
const isScoutSuggestionPanelOpen = computed(
  () =>
    isRightOpen.value &&
    (chatStore.tab === 'newDialog' ||
      chatStore.tab === 'proactiveSuggestions' ||
      chatStore.tab === 'proactiveGeofenceRemovals'),
);

const shouldRender = computed(
  () =>
    (showCreation.value || showRemoval.value) &&
    !isScoutSuggestionPanelOpen.value,
);

const headerText = computed(() => {
  if (showCreation.value && showRemoval.value) {
    return 'Scout has suggestions for your map';
  }
  return showCreation.value
    ? 'Scout found new places to geofence'
    : 'Scout found geofences you may not need';
});

const creationTitle = computed(
  () =>
    `${placeCount.value} suggested geofence${placeCount.value === 1 ? '' : 's'} ready.`,
);
const creationBody = computed(
  () =>
    `Your fleet regularly stops at ${placeCount.value} unmonitored location${placeCount.value === 1 ? '' : 's'}.`,
);
const removalTitle = computed(
  () =>
    `${removalCount.value} geofence${removalCount.value === 1 ? '' : 's'} may be inactive.`,
);
const removalBody =
  'They’ve seen little or no recent activity — review and clean up your list.';

function onReviewCreation() {
  events.trigger(events.names.PLACE_SUGGESTIONS_REVIEW_OPENED, {
    source: 'banner',
    suggestion_count: placeCount.value,
  });
  chatStore.openProactiveSuggestions();
  isRightOpen.value = true;
}

function onReviewRemoval() {
  chatStore.openProactiveGeofenceRemovals();
  isRightOpen.value = true;
}

function onLater() {
  // One "Later" hides both halves, so only report it as a place-suggestion
  // signal when that half was actually on screen. It is a local hide, not a
  // decline — distinct from dismissing a card.
  if (showCreation.value) {
    events.trigger(events.names.PLACE_SUGGESTIONS_BANNER_POSTPONED, {
      suggestion_count: placeCount.value,
    });
  }
  dismissPlace();
  removalSessionDismissed.value = true;
}
</script>

<template>
  <!-- Wrapper is inset on both sides (px-16) so the map's top-left Layers
       control and top-right zoom controls stay clickable; pointer-events-none
       lets clicks through everywhere except the banner card itself. -->
  <div
    v-if="shouldRender"
    class="pointer-events-none absolute inset-x-0 top-0 z-[500] flex justify-center px-16"
    data-testid="scout-map-suggestions-banner"
  >
    <div
      class="pointer-events-auto w-full max-w-xl rounded-b-lg bg-white px-4 py-2 shadow-md"
    >
      <div
        class="flex items-center justify-between gap-2 border-b border-gpx_gray-900 pb-1.5"
      >
        <div class="flex min-w-0 items-center gap-2">
          <SparklesIcon class="text-gpx_red-600 size-4 shrink-0" />
          <span class="truncate text-gpx_sm font-semibold text-gpx_black-50">
            {{ headerText }}
          </span>
        </div>
        <Button
          label="Later"
          severity="secondary"
          outlined
          size="small"
          data-testid="scout-map-suggestions-banner-later"
          @click="onLater"
        />
      </div>

      <div
        v-if="showCreation"
        class="flex items-start gap-3 py-2.5"
        data-testid="scout-map-suggestions-creation-row"
      >
        <span
          class="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-[rgba(242,90,84,0.12)]"
        >
          <MapPinIcon class="text-gpx_red-600 size-3" />
        </span>
        <p class="min-w-0 flex-1 text-gpx_sm text-gpx_black-50">
          <span class="font-semibold">{{ creationTitle }}</span>
          <span class="ml-1">{{ creationBody }}</span>
        </p>
        <Button
          label="Review"
          severity="secondary"
          outlined
          size="small"
          data-testid="scout-map-suggestions-creation-review"
          @click="onReviewCreation"
        />
      </div>

      <div
        v-if="showCreation && showRemoval"
        class="border-t border-gpx_gray-900"
      />

      <div
        v-if="showRemoval"
        class="flex items-start gap-3 py-2.5"
        data-testid="scout-map-suggestions-removal-row"
      >
        <span
          class="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-[#f39c12]"
        >
          <ExclamationTriangleIcon class="size-3 text-white" />
        </span>
        <p class="min-w-0 flex-1 text-gpx_sm text-gpx_black-50">
          <span class="font-semibold">{{ removalTitle }}</span>
          <span class="ml-1">{{ removalBody }}</span>
        </p>
        <Button
          label="Review"
          severity="secondary"
          outlined
          size="small"
          data-testid="scout-map-suggestions-removal-review"
          @click="onReviewRemoval"
        />
      </div>
    </div>
  </div>
</template>
